import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ConfirmModal from "components/common/ConfirmModal";
import {
  MdMoreVert,
  MdOutlineRemoveRedEye,
  MdOutlineEdit,
  MdHistory,
  MdQrCodeScanner,
  MdLogout,
} from "react-icons/md";

export default function ScannerTable({
  data,
  onDelete,
  onRefresh,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  viewDetails,
  loginLogs,
  scanLogs,
  logout,
}) {
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleMenu = (e, rowId) => {
    e.stopPropagation();
    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setOpenUpward(spaceBelow < 150);
      setOpenDropdownId(rowId);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await onDelete(deleteId);
      setDeleteId(null);
      onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="table-scroll-body">
        <table className="custom-table">
          <thead className="custom-table-header  text-gray-800">
            <tr>
              <th className="w-12 text-center">
                <input
                  type="checkbox"
                  className="table-checkbox"
                  onChange={onSelectAll}
                  checked={
                    data.length > 0 && selectedRows.length === data.length
                  }
                />
              </th>
              <th>Name</th>
              <th>Contact</th>
              <th>CNP</th>
              <th>Login Code</th>
              <th>Country</th>
              <th>Gate</th>
              <th className="text-center">Status</th>
              <th className="w-16 text-center"></th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan="9"
                  className="custom-table-cell py-12 text-center text-gray-500"
                >
                  No scanners found
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rowId = item._id || index;
                const isChecked = selectedRows.includes(rowId);
                const isMenuOpen = openDropdownId === rowId;
                const isActive = item.status === "Active" || item.isActive;

                return (
                  <tr key={rowId} className="custom-table-row">
                    <td className="custom-table-cell text-center">
                      <input
                        type="checkbox"
                        className="table-checkbox"
                        checked={isChecked}
                        onChange={() => onSelectRow(rowId)}
                      />
                    </td>

                    <td className="custom-table-cell">
                      {item.name ||
                        `${item.firstName || ""} ${
                          item.lastName || ""
                        }`.trim() ||
                        "N/A"}
                    </td>

                    <td className="custom-table-cell col-contact">
                      <div className="flex flex-col">
                        <span>{item.email || "N/A"}</span>
                        <span className="text-xs">
                          {item.phoneCountryCode
                            ? `${item.phoneCountryCode} ${item.phoneNumber} `
                            : "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="custom-table-cell ">{item.cnp || "N/A"}</td>

                    <td className="custom-table-cell  ">
                      {item.loginCode || item.code || "N/A"}
                    </td>

                    <td className="custom-table-cell ">
                      {item.country || "N/A"}
                    </td>

                    <td className="custom-table-cell ">
                      {item.gateNumber || "N/A"}
                    </td>

                    <td className="custom-table-cell text-center">
                      <span
                        className={`inline-block rounded-[8px] px-3 py-1 text-xs font-semibold ${
                          isActive
                            ? "bg-[#e6f9ed] text-[#22c55e]"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Action Dropdown Cell */}
                    <td className="custom-table-cell relative text-center">
                      <button
                        onClick={(e) => handleToggleMenu(e, rowId)}
                        className="rounded-lg border border-gray-200 bg-white p-1.5 transition-colors hover:bg-gray-50"
                      >
                        <MdMoreVert className="text-lg text-gray-800" />
                      </button>

                      {isMenuOpen && (
                        <div
                          ref={dropdownRef}
                          style={{
                            boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.13)",
                          }}
                          className={`absolute right-12 z-[99] flex w-[150px] flex-col gap-[11px] rounded-lg border border-gray-100 bg-white p-[14px] ${
                            openUpward ? "bottom-0" : "top-0"
                          }`}
                        >
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              viewDetails(item);
                            }}
                            className="hover:text-black flex items-center gap-2.5 text-xs font-medium "
                          >
                            <MdOutlineRemoveRedEye className="text-base " />
                            View
                          </button>

                          <div className="h-[1px] w-full bg-gray-200" />

                          <Link
                            to={`/admin/scanners/edit/${item._id}`}
                            onClick={() => setOpenDropdownId(null)}
                            className="hover:text-black flex items-center gap-2.5 text-xs font-medium "
                          >
                            <MdOutlineEdit className="text-base " />
                            Edit
                          </Link>

                          <div className="h-[1px] w-full bg-gray-200" />
                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              loginLogs(item);
                            }}
                            className="hover:text-black flex items-center gap-2.5 text-xs font-medium "
                          >
                            <MdHistory className="text-base " />
                            Log History
                          </button>

                          <div className="h-[1px] w-full bg-gray-200" />

                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              scanLogs(item);
                            }}
                            className="hover:text-black flex items-center gap-2.5 text-xs font-medium "
                          >
                            <MdQrCodeScanner className="text-base " />
                            Scan History
                          </button>

                          <div className="h-[1px] w-full bg-gray-200" />

                          <button
                            onClick={() => {
                              setOpenDropdownId(null);
                              logout(item);
                            }}
                            className="hover:text-black flex w-full items-center gap-2.5 text-xs font-medium "
                          >
                            <MdLogout className="text-base " />
                            Log Out
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(deleteId)}
        title="Delete Scanner"
        message="This action cannot be undone. Do you want to continue?"
        confirmText="Delete"
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
}
